// src/hooks/useSupabaseTable.ts
import { useEffect, useState } from "react";
import { supabase } from "../client/supabaseClient";
import booksData from '../data/books.json';
import certificationsData from '../data/certifications.json';
import expertiseData from '../data/expertise.json';
import journeyData from '../data/journey.json';

type Options<T> = {
  filter?: (query: any) => any; // optional filter on the query
  realtime?: boolean; // subscribe to real-time updates
};

const fallbackData: Record<string, any[]> = {
  books: booksData,
  certifications: certificationsData,
  expertise: expertiseData,
  journey: journeyData,
};

export function useSupabaseTable<T = any>(table: string, options: Options<T> = {}) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      let query: any = supabase.from(table).select("*");
      if (options.filter) query = options.filter(query);

      const { data: result, error } = await query;
      if (error) throw error;
      setData(result ?? []);
    } catch (err: any) {
      setError(err);
      // Fallback to JSON data if available
      const fallback = fallbackData[table];
      if (fallback) {
        setData(fallback);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    let channel: any = null;
    if (options.realtime) {
      channel = supabase
        .channel(`${table}-changes`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table },
          () => fetchData()
        )
        .subscribe();
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [table]);

  /** Insert a row */
  const insert = async (row: Partial<T>) => {
    const { data: inserted, error } = await supabase.from(table).insert(row).select();
    if (error) throw error;
    setData((prev) => [...prev, ...(inserted ?? [])]);
    return inserted;
  };

  /** Update a row by filter */
  const update = async (updates: Partial<T>, match: Partial<T>) => {
    const { data: updated, error } = await supabase.from(table).update(updates).match(match).select();
    if (error) throw error;
    setData((prev) =>
      prev.map((item) => {
        const shouldUpdate = Object.entries(match).every(([k, v]) => (item as any)[k] === v);
        return shouldUpdate && updated ? { ...(item as any), ...(updated[0] as any) } : item;
      })
    );
    return updated;
  };

  /** Delete a row by filter */
  const remove = async (match: Partial<T>) => {
    const { data: deleted, error } = await supabase.from(table).delete().match(match).select();
    if (error) throw error;
    setData((prev) =>
      prev.filter((item) => !Object.entries(match).every(([k, v]) => (item as any)[k] === v))
    );
    return deleted;
  };

  return { data, loading, error, fetchData, insert, update, remove };
}
